import * as actions from './actions';
import { createReducer } from '@reduxjs/toolkit';
import set from 'lodash.set';

import { getCampaignDataStorePath } from './helpers/index';
import { CampaignReportData } from './helpers/index';
import moment from 'moment';
import { getEnvVar } from 'helpers';

//example campaignReportData
let e = {
  tenant: {
    'tenant-id-123': {
      businessContext: {
        all: {
          days: {
            isLoading: false,
            data: []
          },
          months: {
            isLoading: false,
            data: []
          }
        },
        campaigns: {
          'camp-Id-123': {
            days: {
              isLoading: false,
              data: []
            },
            months: {
              isLoading: false,
              data: []
            }
          }
        }
      }
    }
  }
};

const getParsedCronSchedule = (cronExpression, defaults) => {
  const parts = (cronExpression || '').trim().split(/\s+/);
  const toInt = (value, fallback) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  };

  return {
    minute: toInt(parts[1], defaults.minute),
    hour: toInt(parts[2], defaults.hour),
    dayOfMonth:
      parts[3] && parts[3] !== '?'
        ? toInt(parts[3], defaults.dayOfMonth)
        : defaults.dayOfMonth
  };
};

const getDailyAvailableDate = () => {
  const dailyReportDelayHours =
    parseInt(getEnvVar('ADM_REPORT_DAILY_REPORT_DELAY_HOURS') || '4', 10) || 4;
  const dailyRuntime = getParsedCronSchedule(
    getEnvVar('ADM_REPORT_DAILY_AGGREGATION_RUNTIME') || '0 30 08 ? * *',
    { hour: 8, minute: 30, dayOfMonth: 1 }
  );
  const nowUtc = moment.utc();
  const dailyAggregationCompletionTime = nowUtc
    .clone()
    .startOf('day')
    .hour(dailyRuntime.hour)
    .minute(dailyRuntime.minute)
    .second(0)
    .millisecond(0)
    .add(dailyReportDelayHours, 'hours');

  // Keep cutoff source in UTC (cron + delay), but evaluate availability against local-day time.
  const localCompletionTemplate = dailyAggregationCompletionTime.clone().local();
  const nowLocal = moment();
  const dailyAggregationCompletionTimeLocal = nowLocal
    .clone()
    .startOf('day')
    .hour(localCompletionTemplate.hour())
    .minute(localCompletionTemplate.minute())
    .second(0)
    .millisecond(0);

  const daysBack = nowLocal.isSameOrAfter(dailyAggregationCompletionTimeLocal) ? 1 : 2;

  return nowLocal.clone().subtract(daysBack, 'days').startOf('day');
};

const getMonthlyAvailableDate = () => {
  const monthlyReportDelayHours =
    parseInt(getEnvVar('ADM_REPORT_MONTHLY_REPORT_DELAY_HOURS') || '4', 10) || 4;
  const monthlyRuntime = getParsedCronSchedule(
    getEnvVar('ADM_REPORT_MONTHLY_REPORT_RUNTIME') || '0 30 14 2 * ?',
    { hour: 14, minute: 30, dayOfMonth: 2 }
  );
  const nowUtc = moment.utc();
  const monthlyAggregationCompletionTime = nowUtc
    .clone()
    .startOf('month')
    .date(monthlyRuntime.dayOfMonth)
    .hour(monthlyRuntime.hour)
    .minute(monthlyRuntime.minute)
    .second(0)
    .millisecond(0)
    .add(monthlyReportDelayHours, 'hours');

  const monthsBack = nowUtc.isSameOrAfter(monthlyAggregationCompletionTime) ? 1 : 2;

  return nowUtc
    .clone()
    .startOf('month')
    .subtract(monthsBack, 'months')
    .endOf('month');
};

const getInitialQueryData = () => {
  const maxDailyDate = getDailyAvailableDate();
  return {
    type: 'EMAIL',
    dataHierarchy: 'businessContext',
    dateType: 'DAY',
    fromDate: maxDailyDate.clone().subtract(59, 'days').format('YYYY-MM-DD'),
    toDate: maxDailyDate.format('YYYY-MM-DD'),
    providerMetric: '',
    index: 0
  };
};

const initialState = {
  queryData: getInitialQueryData(),
  campaignName: '',
  campaignReportData: [],
  campaignReportDataCount: 0,
  contextReportData: {},
  getting: true,
  error: {},
  data: null,
  campaignReportCsv: {
    getting: false,
    gettingFileSucceeded: false,
    campaignReportCsvBlob: {},
    isDownloading: false
  },
  messageHistory: {
    getting: false,
    data: {}
  },
  tenantOnly: true,
  emailClickMetrics: {
    getting: false,
    data: [],
    error: null
  },
  clickedUrls: {
    getting: false,
    data: [],
    error: null
  }
};

export default createReducer(initialState, {
  [actions.GET_CONTEXT_REPORT_DATA]: state => {
    state = { ...initialState, queryData: state.queryData, tenantOnly: state.tenantOnly, emailClickMetrics: state.emailClickMetrics };
    return state;
  },
  [actions.GETTING_CONTEXT_REPORT_DATA]: (state, action) => {
    state = {
      ...state,
      getting: true,
      queryData: action?.payload?.queryData,
      campaignName: action?.payload?.queryData?.campaignName
    };
    return state;
  },
  [actions.GETTING_CONTEXT_REPORT_DATA_SUCCEEDED]: (state, action) => {
    const { queryData, contextReportData } = action.payload;
    let path = getCampaignDataStorePath(queryData);
    let contextReport = [];
    if (contextReportData.length > 0) {
      contextReport = new CampaignReportData(contextReportData, queryData);
    }
    set(state.contextReportData, path, contextReport);
    // would prefer to have getting property under the 'path'
    // but store refuses to update that state sometimes
    state.contextReportData.forceSelectorsToUpdate = Date.now();
    state.campaignName = queryData.campaignName;
    state.getting = false;
    return state;
  },
  [actions.GETTING_CONTEXT_REPORT_DATA_FAILED]: (state, action) => {
    state = {
      ...state,
      getting: false,
      error: action.payload.error
    };
    return state;
  },
  [actions.GET_CAMPAIGN_REPORT_DATA]: state => {
    state = {
      ...initialState,
      getting: true,
      queryData: state.queryData,
      tenantOnly: state.tenantOnly,
      emailClickMetrics: state.emailClickMetrics
    };
    return state;
  },

  [actions.GETTING_CAMPAIGN_REPORT_DATA]: (state, action) => {
    state = {
      ...state,
      queryData: action?.payload?.queryData,
      campaignName: action?.payload?.queryData?.campaignName,
      getting: true
    };
    return state;
  },
  [actions.GETTING_CAMPAIGN_REPORT_DATA_SUCCEEDED]: (state, action) => {
    const { campaignReportData } = action.payload;
    state.getting = false;
    if (campaignReportData['data'].length > 0) {
      state.campaignReportData = campaignReportData.data;
      state.campaignReportDataCount = Number(
        campaignReportData.headers['x-ag-totalcount']
      );
    } else {
      state.data = 'No records found';
    }
    return state;
  },
  [actions.GETTING_CAMPAIGN_REPORT_DATA_FAILED]: (state, action) => {
    state = {
      ...state,
      getting: false,
      error: action.payload.error
    };
    return state;
  },
  [actions.GET_CAMPAIGN_REPORT_CSV_FILE]: (state) => {
    state.campaignReportCsv = {
      getting: true,
      isDownloading: true,
      gettingFileSucceeded: false,
      campaignReportCsvBlob: {}
    };
    return state;
  },
  [actions.GETTING_CAMPAIGN_REPORT_CSV_FILE_SUCCEEDED]: (state, action) => {
    state.campaignReportCsv = {
      getting: false,
      gettingFileSucceeded: true,
      campaignReportCsvBlob: action.payload
    };
    return state;
  },
  [actions.GETTING_CAMPAIGN_REPORT_CSV_FILE_FAILED]: state => {
    state.campaignReportCsv = {
      getting: false,
      gettingFileSucceeded: false,
      campaignReportCsvBlob: {}
    };
    return state;
  },
  [actions.DOWNLOADED_CAMPAIGN_REPORT_CSV_FILE]: state => {
    state.campaignReportCsv = {
      ...state.campaignReportCsv,
      isDownloading: false,
      gettingFileSucceeded: false,
      campaignReportCsvBlob: {}
    };
    return state;
  },
  [actions.SWITCH_CAMPAIGN_REPORT_DATA]: (state, action) => {
    state = {
      ...state,
    };
    return state;
  },
  [actions.GET_MESSAGE_HISTORY]: state => {
    state = {
      ...state,
      messageHistory: {
        ...state.messageHistory,
        getting: true,
        data: {}
      }
    };
    return state;
  },
  [actions.GETTING_MESSAGE_HISTORY_SUCCEEDED]: (state, action) => {
    state = {
      ...state,
      messageHistory: {
        ...state.messageHistory,
        getting: false,
        data: action.payload.data
      }
    };
    return state;
  },
  [actions.GETTING_MESSAGE_HISTORY_FAILED]: (state, action) => {
    state = {
      ...state,
      messageHistory: {
        ...state.messageHistory,
        getting: false,
        data: {}
      },
      error: action.payload.error
    };
    return state;
  },
  [actions.SET_TENANT_ONLY]: (state, action) => {
    state = {
      ...state,
      tenantOnly: action.payload
    };
    return state;
  },
  [actions.SET_REPORT_QUERY_DATA]: (state, action) => {
    state = {
      ...state,
      queryData: {
        ...state.queryData,
        ...(action.payload || {})
      }
    };
    return state;
  },
  [actions.GETTING_EMAIL_CLICK_METRICS]: state => {
    state = {
      ...state,
      emailClickMetrics: {
        getting: true,
        data: [],
        error: null
      }
    };
    return state;
  },
  [actions.GETTING_EMAIL_CLICK_METRICS_SUCCEEDED]: (state, action) => {
    state = {
      ...state,
      emailClickMetrics: {
        getting: false,
        data: action.payload.data || [],
        error: null
      }
    };
    return state;
  },
  [actions.GETTING_EMAIL_CLICK_METRICS_FAILED]: (state, action) => {
    state = {
      ...state,
      emailClickMetrics: {
        getting: false,
        data: [],
        error: action.payload.error
      }
    };
    return state;
  },
  [actions.GET_CLICKED_URLS]: state => {
    state = {
      ...state,
      clickedUrls: {
        getting: true,
        data: [],
        error: null
      }
    };
    return state;
  },
  [actions.GETTING_CLICKED_URLS_SUCCEEDED]: (state, action) => {
    state = {
      ...state,
      clickedUrls: {
        getting: false,
        data: action.payload.data || [],
        error: null
      }
    };
    return state;
  },
  [actions.GETTING_CLICKED_URLS_FAILED]: (state, action) => {
    state = {
      ...state,
      clickedUrls: {
        getting: false,
        data: [],
        error: action.payload.error
      }
    };
    return state;
  }
});
